<template>
  <div>
    <!-- 浮動按鈕 -->
    <div
      v-if="!isOpen"
      ref="draggable"
      class="draggable-fab"
      :style="{ top: `${position.y}px`, left: `${position.x}px` }"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <v-btn
        fab
        large
        icon
        @click.stop="handleClick"
      >
        <v-badge
          color="red"
          :content="unreadCount > 99 ? '99+' : unreadCount"
          :value="unreadCount"
          overlap
          class="notification-badge"
          :offset-y="$vuetify.breakpoint.smAndDown ? 12 : 16"
          :offset-x="$vuetify.breakpoint.smAndDown ? 16 : 20"
        >
          <v-icon :size="$vuetify.breakpoint.smAndDown ? 45 : 60">
            $chat
          </v-icon>
        </v-badge>
      </v-btn>
    </div>


    <!-- 通知面板 -->
    <v-card
      v-if="isOpen"
      class="floating-panel"
      elevation="6"
      height="450"
    >
      <v-toolbar dense flat color="primary" dark class="floating-panel-toolbar px-4 px-sm-2">
        <v-toolbar-title>
          <v-avatar size="36">
            <v-img :src="assetURL('/asset/crm/dentco-assistant-icon.png')"/>
          </v-avatar>
          小幫手系統通知
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <AssistantNotificationSettingDialog/>
        <v-btn icon small class="ml-1" @click="togglePanel">
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text ref="notificationList" class="notification-list overflow-y-auto" style="max-height: 352px;">
        <div v-if="!hasMoreHistory" class="text-center text-caption primary--text mb-2">
          已經到頂囉！<br>若想看完整內容請至「右上角小鈴噹」觀看詳情
        </div>
        <div
          v-for="(msg, index) in messages"
          :key="msg.id"
          :ref="msg.id === firstUnreadId ? 'firstUnread' : null"
        >
          <div v-if="shouldShowDateLabel(index)" class="text-caption text--secondary mb-1 text-center">
            {{ formatDateLabel(msg.date) }}
          </div>
          <div v-if="msg.id === firstUnreadId" class="mb-1 text-center red-6--text font-weight-bold">
            以下為尚未閱讀的訊息
          </div>
          <div class="mb-2 d-flex system-message bubble-with-tail">
            <NotificationMessage :type="msg.type" :message="msg" style="width: 80%;"/>
            <div style="width: 20%;" class="align-self-end px-1">
              <div>{{ formatTime(msg.date) }}</div>
            </div>
          </div>

        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { assetURL } from '@/utils/filters';
import NotificationMessage from "@/components/Global/AssistantWidget/NotificationMessage.vue";
import { NotificationSoundManager } from "@/utils/NotificationSoundManager";
import { getChatNotificationAPI, getNotificationSoundSettingAPI, setChatNotificationReadAPI } from "@/api";
import { mapGetters } from "vuex";
import AssistantNotificationSettingDialog
  from "@/components/Global/AssistantWidget/AssistantNotificationSettingDialog.vue";
import { checkUserExist, flashTitle } from "@/utils/tool";

export default {
  name: 'AssistantWidget',

  components: {
    AssistantNotificationSettingDialog,
    NotificationMessage
  },

  data() {
    // 幾秒讀取一次，單位(秒)
    const pollingInterval = 60;

    return {
      assetURL,
      isOpen: false,
      messages: [],
      unreadCount: 0,
      hasMoreHistory: true,
      firstUnreadId: null,
      pollingInterval: pollingInterval * 1000,

      position: {x: window.innerWidth - 100, y: window.innerHeight - 150},
      offset: {x: 0, y: 0},
      dragging: false,
      wasDragged: false,
      dragThreshold: 5, // 判定是否為拖曳的最小移動距離
      startPos: {x: 0, y: 0},
    };
  },

  computed: {
    ...mapGetters([
      'axiosParamData'
    ])
  },

  watch: {
    isOpen(newVal) {
      this.$nextTick(() => {
        const list = this.$refs.notificationList;
        if (newVal && list) {
          list.addEventListener('scroll', this.handleScroll);
        } else if (list) {
          list.removeEventListener('scroll', this.handleScroll);
        }
      });

      if (newVal) {
        this.$nextTick(() => {
          this.scrollToProperPosition();
        });

        if (this.unreadCount) {
          this.markAllAsRead();
        } else {
          this.firstUnreadId = null;
        }
      }
    }
  },

  async created() {
    // 初始化通知音效設定
    try {
      const {data} = await getNotificationSoundSettingAPI(this.axiosParamData);
      NotificationSoundManager.initFromApi(data);
    } catch (e) {
      console.warn('載入通知音效設定失敗', e);
    }
    await this.fetchMessages({direction: 'new', cursor: null, pageSize: 10});
    this.polling = setInterval(async () => {
      const lastId = this.messages.length > 0 ? this.messages[this.messages.length - 1].id : null;
      await this.fetchMessages({direction: 'new', cursor: lastId, pageSize: null});
    }, this.pollingInterval);
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    clearInterval(this.polling);
    const list = this.$refs.notificationList;
    list && list.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    clampPosition() {
      const btnSize = 80; // 實際按鈕尺寸
      const minX = 0;
      const maxX = window.innerWidth - btnSize;
      const minY = 0;
      const maxY = window.innerHeight - btnSize;

      this.position.x = Math.min(Math.max(this.position.x, minX), maxX);
      this.position.y = Math.min(Math.max(this.position.y, minY), maxY);
    },
    handleResize() {
      this.clampPosition();
    },
    startDrag(e) {
      const point = this.getPoint(e);
      this.dragging = true;
      this.wasDragged = false;
      this.startPos = {x: point.x, y: point.y};
      this.offset.x = point.x - this.position.x;
      this.offset.y = point.y - this.position.y;

      const moveHandler = this.onDrag;
      const endHandler = this.stopDrag;

      if (e.type === "touchstart") {
        document.addEventListener("touchmove", moveHandler);
        document.addEventListener("touchend", endHandler);
      } else {
        document.addEventListener("mousemove", moveHandler);
        document.addEventListener("mouseup", endHandler);
      }
    },
    onDrag(e) {
      const point = this.getPoint(e);
      const dx = point.x - this.startPos.x;
      const dy = point.y - this.startPos.y;

      if (Math.sqrt(dx * dx + dy * dy) > this.dragThreshold) {
        this.wasDragged = true;
      }

      if (!this.dragging) return;

      this.position.x = point.x - this.offset.x;
      this.position.y = point.y - this.offset.y;

      this.clampPosition(); // 加邊界限制
    },
    stopDrag(e) {
      this.dragging = false;

      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("touchend", this.stopDrag);
    },
    handleClick() {
      // 如果剛剛是拖曳行為，就不要展開 panel
      if (this.wasDragged) return;
      this.isOpen = true;
    },
    getPoint(e) {
      if (e.touches && e.touches.length > 0) {
        return {x: e.touches[0].clientX, y: e.touches[0].clientY};
      }
      return {x: e.clientX, y: e.clientY};
    },

    togglePanel() {
      this.isOpen = !this.isOpen;
    },
    async markAllAsRead() {
      if (!this.messages.length) return;

      const latestId = this.messages[this.messages.length - 1].id;
      try {
        await setChatNotificationReadAPI({
          ...this.axiosParamData,
          cursor: latestId
        })

        // 視覺上設為已讀
        this.messages.forEach(msg => {
          msg.isRead = true;
        });

        this.unreadCount = 0;
      } catch (e) {
        console.warn('標記已讀失敗', e);
      }
    },
    async fetchMessages({direction = 'new', cursor = null, pageSize = 10} = {}) {
      try {
        const res = await getChatNotificationAPI({
          ...this.axiosParamData,
          direction,
          cursor,
          pageSize
        });
        const newMsgs = res.data.contents;
        const totalUnreadCount = res.data.total_unread_count;
        const existIds = new Set(this.messages.map(m => m.id));
        const filteredMsgs = newMsgs.filter(m => !existIds.has(m.id));

        // 分頁 hasMoreHistory 判斷
        if (direction === 'old' || (direction === 'new' && cursor === null)) {
          this.hasMoreHistory = res.data.has_more_history;
        }

        if (direction === 'new') {
          this.messages = [...this.messages, ...filteredMsgs];
        } else if (direction === 'old') {
          this.messages = [...filteredMsgs, ...this.messages];
        } else {
          this.messages = newMsgs;
        }

        const newCount = newMsgs.filter(m => !m.isRead).length;

        if (direction === 'new') {
          this.unreadCount = totalUnreadCount
          if (newCount) {
            NotificationSoundManager.playIfAllowed({
              type: newMsgs[0]?.type, // 取最新那筆的 type
              widgetOpen: this.isOpen
            });
            if (!checkUserExist()) {
              const count = totalUnreadCount > 99 ? '99+' : totalUnreadCount;
              flashTitle(count, '醫師小幫手CRM系統');
            }

            // 設定第一個未讀
            const unread = this.messages.find(msg => !msg.isRead);
            this.firstUnreadId = unread ? unread.id : null;


            if (this.isOpen) {
              await this.markAllAsRead();
            }
          }
        }
      } catch (e) {
        console.warn('取得對話型通知失敗:\n', e);
      }
    },
    formatTime(datetime) {
      return this.$moment(datetime).format('HH:mm');
    },
    formatDateLabel(datetime) {
      const date = this.$moment(datetime);
      const today = this.$moment();
      const yesterday = this.$moment().subtract(1, 'days');
      if (date.isSame(today, 'day')) return '今日';
      if (date.isSame(yesterday, 'day')) return '昨日';
      return date.format('YYYY/MM/DD');
    },
    shouldShowDateLabel(index) {
      if (index === 0) return true;
      const current = this.$moment(this.messages[index].date);
      const previous = this.$moment(this.messages[index - 1].date);
      return !current.isSame(previous, 'day');
    },
    handleScroll(e) {
      const el = e.target;
      if (el.scrollTop === 0 && !this.isLoading && this.hasMoreHistory) {
        this.loadMoreMessages();
      }
    },
    async loadMoreMessages() {
      this.isLoading = true;
      const list = this.$refs.notificationList;
      const oldScrollHeight = list.scrollHeight;
      const oldScrollTop = list.scrollTop;
      const firstId = this.messages[0]?.id;
      await this.fetchMessages({direction: 'old', cursor: firstId});
      this.$nextTick(() => {
        const newScrollHeight = list.scrollHeight;
        list.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
      });
      this.isLoading = false;
    },
    scrollToProperPosition() {
      const el = this.$refs.firstUnread;
      const list = this.$refs.notificationList;

      if (Array.isArray(el)) {
        if (el.length > 0) {
          el[0].scrollIntoView({block: 'start'});
        } else if (list) {
          list.scrollTop = list.scrollHeight;
        }
      } else if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({block: 'start'});
      } else if (list) {
        list.scrollTop = list.scrollHeight;
      }
    },
    clearUnread() {
      this.firstUnreadId = null;
    }
  },
};
</script>

<style scoped>
.floating-panel {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 2000;
  border-radius: 8px;
}

.floating-panel-toolbar {
  background: transparent linear-gradient(90deg, #33B6B0 0%, #009EEB 100%) 0% 0% no-repeat padding-box;
}

.notification-list {
  max-height: 350px;
  overflow-y: auto;
}

.bubble-with-tail {
  position: relative;
}

.bubble-with-tail::before {
  content: "";
  position: absolute;
  left: -8px; /* 調整位置 */
  top: 24px; /* 距離上方 */
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #EFEFEF; /* 對應卡片底色 */
}

.draggable-fab {
  position: fixed;
  z-index: 2000;
  cursor: grab;
}

.floating-panel {
  position: fixed;
  width: 320px;
  z-index: 2000;
}
</style>
